<script setup lang="ts">
import { ref, watch } from 'vue';

import { personLabel, type PickablePerson } from '@/utils/person';

/**
 * 选人控件：输入即筛选。
 *
 * 原来两处都是 109 项的原生下拉，试用反馈「责任人要下拉一个个查找」。
 * 改成 input + 共享 datalist：打字就过滤，浏览器原生行为，键盘可用；
 * 同时整张计划表只渲染一份人员列表（原先每行一份，200 行就是两万个 option）。
 *
 * 只认「唯一匹配」：输入的名字对不上人或对上多个，都退回原值并报出来，
 * 不猜——责任人写错了通知就发给别人。
 */
const props = defineProps<{
  people: PickablePerson[];
  modelValue: number | null;
  /** 旧数据里没有账号的文本责任人：显示出来，但不是可选中的值 */
  legacyName?: string | null;
  disabled?: boolean;
  /** 无障碍名称；不叫 ariaLabel 是因为它会和 HTML 原生 aria-label 属性混淆 */
  label: string;
  /** 共享 datalist 的 id，由使用方渲染一次 */
  listId: string;
  /** 单元格保存状态样式（saving / saved / error） */
  tone?: string;
  /** 空值提示；表格里默认「未分配」，添加成员之类的场景要换成「输入姓名」 */
  placeholder?: string;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: number | null];
  /** 输入的名字无法唯一匹配到账号 */
  unresolved: [text: string];
}>();

const text = ref('');

function sync(): void {
  const current = props.people.find((person) => person.id === props.modelValue);
  text.value = current ? personLabel(current) : (props.legacyName ?? '');
}

watch(
  () => [props.modelValue, props.legacyName, props.people] as const,
  sync,
  { immediate: true },
);

function commit(): void {
  const value = text.value.trim();
  if (!value) {
    // 本来就是无账号的文本责任人：清空不代表要改什么，恢复显示即可
    if (props.modelValue === null) { sync(); return; }
    emit('update:modelValue', null);
    return;
  }
  const byLabel = props.people.filter((person) => personLabel(person) === value);
  const matches = byLabel.length
    ? byLabel
    : props.people.filter((person) => person.displayName === value);
  if (matches.length !== 1) {
    emit('unresolved', value);
    sync();
    return;
  }
  const person = matches[0]!;
  if (person.id === props.modelValue) sync();
  else emit('update:modelValue', person.id);
}
</script>

<template>
  <input
    v-model="text"
    class="cell-input person-picker"
    :class="tone"
    type="search"
    :list="listId"
    :disabled="disabled"
    :aria-label="label"
    :title="legacyName && modelValue === null
      ? `${legacyName}（未关联账号；选一个系统里的人即可绑定）`
      : undefined"
    :placeholder="placeholder ?? '未分配'"
    autocomplete="off"
    @change="commit"
    @keyup.enter="commit"
  >
</template>

<style scoped>
.person-picker { width: 100%; }
</style>
